/* eslint-disable jsx-a11y/heading-has-content */
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TEAM_API_BASE, WBS_ELEMENT_BASE_URL } from '../../../../api/api';
import { usePushWBSElement } from '../../../../api/pushWBS';
import { useGenericAPI } from '../../../../api/useGenericApi';
import { useWBSElementByID } from '../../../../api/useWBSElement';
import { ProButton } from '../../../../atoms/Button/ProButton';
import { ProCol } from '../../../../atoms/Grid/Col';
import { ProRow } from '../../../../atoms/Grid/Row';
import { ProIcon } from '../../../../atoms/Icons/Icon';
import { ProIconData, ProIconDataButtons } from '../../../../atoms/Icons/IconData';
import { ProStatus } from '../../../../atoms/ProjectStatus/ProStatus';
import { ProSpinner } from '../../../../atoms/ProSpinner/Spinner';
import { ApplicationElementType, getBaseBCItemForType } from '../../../../components/DetailWindow/breadCrump.helpers';
import { ProPage } from '../../../../components/Page/Page';
import { ProjectContext } from '../../../../contexts/ProjectContext';
import { WBSContext } from '../../../../contexts/WBSContext';
import { getIconForType } from '../../../../modules/Windows/WBSElement/WBSElement.helpers';
import { ProIntent, ProSize } from '../../../../utils/enums';
import { Components } from '../../../../utils/types';

export const SCMeetingWBSDetails = () => {
    const { wbsElementId, deltaId } = useParams<{ wbsElementId?: string; deltaId?: string }>();
    const { data: detailsWBS, error, loading } = useWBSElementByID(wbsElementId, deltaId);
    const { selectedWBSStructureId, selectedDeltaWBSStructureId } = useContext(WBSContext);
    const {
        error: pushError,
        loading: pushLoading,
        push,
        success,
    } = usePushWBSElement(detailsWBS, selectedDeltaWBSStructureId);

    useEffect(() => {
        console.log('selectedDeltaWBSStructureId', selectedDeltaWBSStructureId, selectedWBSStructureId);
    }, [selectedDeltaWBSStructureId]);

    if (loading || !detailsWBS)
        return (
            <ProPage
                breadCrump={[]}
                name={'Steering Committee Meeting'}
                icon={ProIconData.package_fill}
                isHidden={false}
                displayProjectSelector
            >
                <ProSpinner />
            </ProPage>
        );

    if (error)
        return (
            <ProPage
                breadCrump={[]}
                name={'Steering Committee Meeting'}
                icon={ProIconData.package_fill}
                isHidden={false}
                displayProjectSelector
            >
                error
            </ProPage>
        );

    return (
        <SCMeetingDetails
            detailsWBS={detailsWBS}
            push={push}
            pushError={pushError}
            pushLoading={pushLoading}
            pushSuccess={success}
        />
    );
};

export const SCMeetingTeamDetails = () => {
    const { selectedWBSStructureId, selectedDeltaWBSStructureId } = useContext(WBSContext);
    const { selectedTeam } = useContext(ProjectContext);
    const {
        data: wbsElements,
        error,
        loading,
    } = useGenericAPI<Components.Schemas.WBSElementDeltaDTO[]>(
        `${WBS_ELEMENT_BASE_URL}${TEAM_API_BASE}/${selectedTeam?.id}/Baseline/${selectedWBSStructureId}/Delta`,
        selectedDeltaWBSStructureId
    );

    const {
        error: pushError,
        loading: pushLoading,
        push,
        success,
    } = usePushWBSElement(wbsElements ? wbsElements[0] : undefined, selectedDeltaWBSStructureId);

    if (!wbsElements || loading || error) return <ProSpinner />;

    return (
        <SCMeetingDetails
            detailsWBS={wbsElements[0]}
            push={push}
            pushError={pushError}
            pushLoading={pushLoading}
            pushSuccess={success}
        />
    );
};

type Props = {
    detailsWBS: Components.Schemas.WBSElementDeltaDTO;
    push: () => void;
    pushError?: string;
    pushLoading: boolean;
    pushSuccess: boolean;
};

export const SCMeetingDetails = ({ detailsWBS, push, pushError, pushLoading, pushSuccess }: Props) => {
    const { t } = useTranslation();

    return (
        <ProPage
            breadCrump={getBaseBCItemForType([ApplicationElementType.Team], t)}
            name={'Steering Committee Meeing'}
            icon={ProIconData.package_fill}
            isHidden={false}
            displayProjectSelector
        >
            <div className="SCMeeting">
                <h2 className="SCMeeting__header">
                    {
                        <ProIcon
                            size={ProSize.Medium}
                            icon={getIconForType(detailsWBS.wbsElementType)}
                            className="SCMeeting__headerIcon"
                        />
                    }{' '}
                    {detailsWBS.name}
                </h2>
                <ProRow spacer>
                    <ProCol span={2}>
                        <h2 className="SCMeeting__firstHead"></h2>
                        <ul>
                            <li>
                                <ProIcon icon={ProIconData.benefit_fill} size={ProSize.Large} />
                                <h3>Benefit</h3>
                            </li>
                            <li>
                                <ProIcon icon={ProIconData.scope_fill} size={ProSize.Large} />
                                <h3>Scope</h3>
                            </li>
                            <li>
                                <ProIcon icon={ProIconData.quality_fill} size={ProSize.Large} />
                                <h3>Quality</h3>
                            </li>
                            <li>
                                <ProIcon icon={ProIconData.time_fill} size={ProSize.Large} />
                                <h3>Time</h3>
                            </li>
                            <li>
                                <ProIcon icon={ProIconData.cost_fill} size={ProSize.Large} />
                                <h3>Cost</h3>
                            </li>
                        </ul>
                    </ProCol>
                    <ProCol span={3} className="SCMeeting__box">
                        <h2>Current Situation</h2>
                        <ProRow>
                            <ProCol span={4}>
                                <h3>Forcast</h3>
                            </ProCol>
                            <ProCol span={4}>
                                <h3>Plan</h3>
                            </ProCol>
                            <ProCol span={4}>
                                <h3>Deviation</h3>
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>??? BP</ProCol>
                            <ProCol span={4}>??? BP</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaTimePercent}
                                    state={detailsWBS?.deltaTimeState}
                                    icon={ProIconData.time_fill}
                                />
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>{detailsWBS.scopePoints} SP</ProCol>
                            <ProCol span={4}>{detailsWBS.wbsElementDelta?.scopePoints} SP</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaTimePercent}
                                    state={detailsWBS?.deltaTimeState}
                                    icon={ProIconData.time_fill}
                                />
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>{detailsWBS?.actualQuality} %</ProCol>
                            <ProCol span={4}>{detailsWBS?.quality} %</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaQualityPercent}
                                    state={detailsWBS?.deltaQualityState}
                                    icon={ProIconData.quality_fill}
                                />
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>{detailsWBS?.actualDeltaTime}</ProCol>
                            <ProCol span={4}>{detailsWBS?.deltaTime}</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaTimePercent}
                                    state={detailsWBS?.deltaTimeState}
                                    icon={ProIconData.time_fill}
                                />
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>{detailsWBS?.actualDeltaCost}</ProCol>
                            <ProCol span={4}>{detailsWBS?.deltaCost}</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaCostPercent}
                                    state={detailsWBS?.deltaCostState}
                                    icon={ProIconData.cost_fill}
                                />
                            </ProCol>
                        </ProRow>
                    </ProCol>
                    <ProCol span={3} className="SCMeeting__box">
                        <h2>Project end</h2>
                        <ProRow>
                            <ProCol span={4}>
                                <h3>Forcast</h3>
                            </ProCol>
                            <ProCol span={4}>
                                <h3>Plan</h3>
                            </ProCol>
                            <ProCol span={4}>
                                <h3>Deviation</h3>
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>???</ProCol>
                            <ProCol span={4}>???</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaTimePercent}
                                    state={detailsWBS?.deltaTimeState}
                                    icon={ProIconData.time_fill}
                                />
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>???</ProCol>
                            <ProCol span={4}>???</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaTimePercent}
                                    state={detailsWBS?.deltaTimeState}
                                    icon={ProIconData.time_fill}
                                />
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>??? %</ProCol>
                            <ProCol span={4}>??? %</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaQualityPercent}
                                    state={detailsWBS?.deltaQualityState}
                                    icon={ProIconData.quality_fill}
                                />
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>???</ProCol>
                            <ProCol span={4}>???</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaTimePercent}
                                    state={detailsWBS?.deltaTimeState}
                                    icon={ProIconData.time_fill}
                                />
                            </ProCol>
                        </ProRow>
                        <ProRow alignment="center">
                            <ProCol span={4}>???</ProCol>
                            <ProCol span={4}>???</ProCol>
                            <ProCol span={4}>
                                <ProStatus
                                    percent={detailsWBS?.deltaCostPercent}
                                    state={detailsWBS?.deltaCostState}
                                    icon={ProIconData.cost_fill}
                                />
                            </ProCol>
                        </ProRow>
                    </ProCol>
                </ProRow>
                <ProRow>
                    <ProCol span={8} alignment="flex-end" direction="row" verticalAlign="bottom">
                        <ProButton text="Cancle" mainIntent={ProIntent.Gray} />
                        {!pushLoading && (
                            <ProButton
                                text="Push"
                                rightIcon={ProIconDataButtons.two_check_s}
                                mainIntent={ProIntent.Secondary}
                                onClick={push}
                            />
                        )}
                        {pushLoading && <ProSpinner />}
                    </ProCol>
                </ProRow>
            </div>
        </ProPage>
    );
};
